import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Calendar,
  Clock,
  Landmark,
  Loader2,
  Plug,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormField } from '@/components/FormField'
import { Modal } from '@/components/Modal'
import { Toolbar } from '@/components/Toolbar'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { useCoa, useCreateSubcategory } from '@/features/coa/queries'
import { uploadApi } from '@/features/upload/api'
import type {
  ClassifiedRow,
  ClassifiedTable,
  CoaSuggestion,
} from '@/features/upload/types'
import { plaidApi } from '@/features/plaid/api'
import {
  useDeletePlaidConnection,
  usePlaidConnections,
  useSchedulePlaid,
} from '@/features/plaid/queries'
import { loadPlaid } from '@/features/plaid/loadPlaidScript'
import type { PlaidConnection, PlaidExchangeResponse } from '@/features/plaid/types'
import type { ClassificationGroup, CoaSelection } from '@/pages/upload/ClassificationGroupCard'
import { ClassifyStepper } from '@/pages/upload/ClassifyStepper'

type Step = 'idle' | 'loading' | 'classify' | 'saving'

const PLAID_TERMS_KEY = 'plaid_terms_accepted'

const errMsg = (err: unknown, fallback = 'Action failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

function regexNormalizeDescription(desc: string): string {
  return desc
    .replace(/(Ref|ID|#)\s*#?\s*[A-Z0-9-]{3,}/gi, '')
    .replace(/\d{4,}/g, '')
    .replace(/\d{1,2}\/\d{1,2}(\/\d{2,4})?/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

function isValidPrediction(s: CoaSuggestion[]): boolean {
  return s.some(
    (x) =>
      x.main_category &&
      x.main_category !== 'Uncategorized' &&
      x.subcategory &&
      x.subcategory !== 'General',
  )
}

function filterValid(s: CoaSuggestion[]): CoaSuggestion[] {
  return s.filter(
    (x) =>
      x.main_category &&
      x.main_category !== 'Uncategorized' &&
      x.subcategory &&
      x.subcategory !== 'General',
  )
}

export function PlaidPage() {
  const { toast } = useToast()
  const navigate = useNavigate()
  const connectionsQ = usePlaidConnections()
  const deleteConn = useDeletePlaidConnection()
  const scheduleConn = useSchedulePlaid()
  const { data: taxonomyRows } = useCoa()
  const createSubcategory = useCreateSubcategory()
  const taxonomy = useMemo(() => taxonomyRows ?? [], [taxonomyRows])

  // Form
  const [statementType, setStatementType] = useState<'BS' | 'CCS' | ''>('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [termsOpen, setTermsOpen] = useState(false)
  const [limitOpen, setLimitOpen] = useState(false)

  // Pipeline state
  const [step, setStep] = useState<Step>('idle')
  const [busyText, setBusyText] = useState('')
  const [classifiedTables, setClassifiedTables] = useState<ClassifiedTable[]>([])
  const [groups, setGroups] = useState<ClassificationGroup[]>([])
  const [selections, setSelections] = useState<Record<string, CoaSelection | null>>({})
  const [plaidContext, setPlaidContext] = useState<{
    statementType: string
    period: string | undefined
    plaidItemIds: number[]
  } | null>(null)
  const [redirectModal, setRedirectModal] = useState(false)

  // Schedule modal
  const [scheduleFor, setScheduleFor] = useState<PlaidConnection | null>(null)
  const [schedValue, setSchedValue] = useState('1')
  const [schedUnit, setSchedUnit] = useState<'minutes' | 'hours' | 'days'>('days')
  const [schedHour, setSchedHour] = useState('8')
  const [schedMinute, setSchedMinute] = useState('0')
  const [schedAm, setSchedAm] = useState<'AM' | 'PM'>('AM')

  const connections = connectionsQ.data ?? []

  useEffect(() => {
    if (!redirectModal) return
    const t = setTimeout(() => {
      setRedirectModal(false)
      navigate('/view-statements')
    }, 5000)
    return () => clearTimeout(t)
  }, [redirectModal, navigate])

  const startConnect = async () => {
    if (!localStorage.getItem(PLAID_TERMS_KEY)) {
      setTermsOpen(true)
      return
    }
    if (!statementType || !fromDate || !toDate) {
      toast({
        variant: 'destructive',
        title: 'Missing information',
        description: 'Choose statement type and a full date range.',
      })
      return
    }
    setStep('loading')
    setBusyText('Creating Plaid link…')
    try {
      const { link_token } = await plaidApi.createLinkToken()
      const Plaid = await loadPlaid()
      const handler = Plaid.create({
        token: link_token,
        onSuccess: async (publicToken, metadata) => {
          setBusyText('Connecting and fetching transactions…')
          try {
            const data = await plaidApi.exchangePublicToken({
              public_token: publicToken,
              metadata,
              fromDate,
              toDate,
              statementType,
              period: toDate ? toDate.substring(0, 7) : null,
            })
            await onExchange(data)
            connectionsQ.refetch()
          } catch (err) {
            if (err instanceof ApiError && err.status === 402) {
              setLimitOpen(true)
              setStep('idle')
              return
            }
            toast({ variant: 'destructive', title: 'Import failed', description: errMsg(err) })
            setStep('idle')
          }
        },
        onExit: () => setStep('idle'),
      })
      handler.open()
    } catch (err) {
      toast({ variant: 'destructive', title: 'Plaid Link failed', description: errMsg(err) })
      setStep('idle')
    }
  }

  const onExchange = async (data: PlaidExchangeResponse) => {
    if (data.transactionsPending) {
      toast({ title: 'Bank connected', description: data.message ?? 'Transactions are syncing - check back shortly.' })
      setStep('idle')
      return
    }
    if (!data.tables || data.tables.length === 0) {
      toast({ title: 'No transactions', description: 'No new transactions in the selected period.' })
      setStep('idle')
      return
    }
    const ctx = {
      statementType: data.statementType ?? statementType,
      period: data.period,
      plaidItemIds: data.plaidItemId
        ? [data.plaidItemId]
        : data.itemMeta?.map((m) => m.plaidItemId) ?? [],
    }
    setPlaidContext(ctx)
    await analyzeAndClassify(
      data.tables.map((t) => ({
        name: t.name,
        rows: t.rows.map((r) => ({
          date: String(r.date ?? ''),
          description: String(r.description ?? ''),
          amount: String(r.amount ?? ''),
        })) as ClassifiedRow[],
      })),
    )
  }

  const syncAll = async () => {
    setStep('loading')
    setBusyText('Fetching latest transactions from all accounts…')
    try {
      const data = await plaidApi.syncAll()
      await onExchange(data)
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        setLimitOpen(true)
        setStep('idle')
        return
      }
      toast({ variant: 'destructive', title: 'Sync failed', description: errMsg(err) })
      setStep('idle')
    }
  }

  const syncOne = async (id: number) => {
    setStep('loading')
    setBusyText('Fetching latest transactions…')
    try {
      const data = await plaidApi.syncOne(id)
      await onExchange(data)
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        setLimitOpen(true)
        setStep('idle')
        return
      }
      toast({ variant: 'destructive', title: 'Sync failed', description: errMsg(err) })
      setStep('idle')
    }
  }

  const analyzeAndClassify = async (tables: ClassifiedTable[]) => {
    setBusyText('Checking for duplicates…')
    let workingTables = tables
    try {
      const dup = await uploadApi.checkDuplicates(tables)
      if (dup.duplicates.length > 0 && dup.newTransactions.length === 0) {
        toast({ title: 'All duplicates', description: 'These transactions are already saved.' })
        setStep('idle')
        return
      }
      if (dup.duplicates.length > 0 && dup.newTransactions.length > 0) {
        const newSet = new Set(dup.newTransactions.map((n) => `${n.originalTableIndex}-${n.originalRowIndex}`))
        workingTables = tables
          .map((t, i) => ({
            ...t,
            rows: t.rows.filter((_, r) => newSet.has(`${i}-${r}`)) as ClassifiedRow[],
          }))
          .filter((t) => t.rows.length > 0)
      }
    } catch {
      /* swallow */
    }

    setBusyText('Analyzing descriptions…')
    const allDescriptions: string[] = []
    for (const t of workingTables)
      for (const r of t.rows) if (r.description) allDescriptions.push(r.description.trim())

    let matches: Awaited<ReturnType<typeof uploadApi.matchBatch>> = {}
    if (allDescriptions.length > 0) {
      try {
        matches = await uploadApi.matchBatch(allDescriptions)
      } catch {
        /* silent */
      }
    }

    const unmatchedRows: Array<{
      tIdx: number
      rIdx: number
      row: ClassifiedRow
      description: string
    }> = []

    workingTables.forEach((t, tIdx) => {
      t.rows.forEach((r, rIdx) => {
        const desc = (r.description ?? '').trim()
        const rule = matches[desc]
        if (rule && rule.coa) {
          r.general_category = rule.general_category
          r.category = rule.general_category
          r.coa = rule.coa
        } else if (desc) {
          r.coa = null
          r.category = r.category ?? rule?.general_category ?? ''
          unmatchedRows.push({ tIdx, rIdx, row: r, description: desc })
        }
      })
    })

    setClassifiedTables(workingTables)

    if (unmatchedRows.length === 0) {
      await save(workingTables)
      return
    }

    setBusyText('Grouping similar transactions…')
    const uniqueDescriptions = Array.from(new Set(unmatchedRows.map((u) => u.description)))
    let normalizedMap: Record<string, string> = {}
    try {
      const r = await uploadApi.normalizeDescriptionBatch(uniqueDescriptions)
      normalizedMap = r.normalized ?? {}
    } catch {
      /* fallback to regex */
    }

    const groupMap = new Map<string, ClassificationGroup>()
    for (const u of unmatchedRows) {
      const norm = normalizedMap[u.description] ?? regexNormalizeDescription(u.description)
      const exist = groupMap.get(norm)
      const item = { ...u.row, tIdx: u.tIdx, rIdx: u.rIdx }
      if (exist) exist.children.push(item)
      else
        groupMap.set(norm, {
          key: norm || `r-${u.tIdx}-${u.rIdx}`,
          main: item,
          children: [],
          suggestions: [],
        })
    }

    setBusyText('AI is mapping Chart of Accounts…')
    const payload = Array.from(groupMap.values()).map((g) => ({
      description: g.main.description ?? '',
      category: g.main.category ?? undefined,
    }))
    let predictions: Record<string, CoaSuggestion[]> = {}
    try {
      const r = await uploadApi.predictCoaBatch(payload)
      predictions = r.predictions ?? {}
    } catch {
      /* silent */
    }

    const findPrediction = (desc: string): CoaSuggestion[] => {
      const trimmed = desc.trim()
      if (predictions[desc]) return predictions[desc]
      if (predictions[trimmed]) return predictions[trimmed]
      const entry = Object.entries(predictions).find(([k]) => k.trim() === trimmed)
      return entry ? entry[1] : []
    }

    const builtGroups = Array.from(groupMap.values()).map((g) => {
      const raw = findPrediction(g.main.description ?? '')
      const suggestions = raw.length > 0 && isValidPrediction(raw) ? filterValid(raw) : []
      return { ...g, suggestions }
    })

    const initialSelections: Record<string, CoaSelection | null> = {}
    for (const g of builtGroups) {
      if (g.suggestions.length > 0) {
        const top = g.suggestions[0]
        initialSelections[g.key] = {
          statement_type: top.statement_type,
          main_category: top.main_category,
          subcategory: top.subcategory,
          account_code: top.account_code ?? '',
        }
      } else {
        initialSelections[g.key] = null
      }
    }

    setGroups(builtGroups)
    setSelections(initialSelections)
    setStep('classify')
  }

  const saveClassifications = async () => {
    for (const g of groups) {
      if (!selections[g.key]) {
        toast({
          variant: 'destructive',
          title: 'COA required',
          description: `Pick a Chart of Account for "${g.main.description ?? '(no description)'}"`,
        })
        return
      }
    }
    const rowSel: Record<string, CoaSelection> = {}
    for (const g of groups) {
      const sel = selections[g.key]
      if (!sel) continue
      rowSel[`${g.main.tIdx}-${g.main.rIdx}`] = sel
      for (const c of g.children) rowSel[`${c.tIdx}-${c.rIdx}`] = sel
    }
    const updated = classifiedTables.map((t, tIdx) => ({
      ...t,
      rows: t.rows.map((r, rIdx) => {
        if (r.coa) return r
        const sel = rowSel[`${tIdx}-${rIdx}`]
        if (!sel) return r
        return {
          ...r,
          category: sel.subcategory || r.category,
          general_category: sel.main_category || r.general_category,
          coa: {
            statement_type: sel.statement_type,
            main_category: sel.main_category,
            subcategory: sel.subcategory,
            account_code: sel.account_code,
          },
        }
      }),
    }))
    await save(updated)
  }

  const save = async (tables: ClassifiedTable[]) => {
    if (!plaidContext) return
    setStep('saving')
    setBusyText('Saving to ledger…')
    try {
      await plaidApi.confirm({
        tables,
        statementType: plaidContext.statementType,
        period: plaidContext.period,
        plaidItemIds: plaidContext.plaidItemIds,
      })
      setStep('idle')
      setGroups([])
      setSelections({})
      setClassifiedTables([])
      setRedirectModal(true)
    } catch (err) {
      toast({ variant: 'destructive', title: 'Save failed', description: errMsg(err) })
      setStep('classify')
    }
  }

  const handleCreateTaxonomy = async (draft: {
    statement_type: string
    main_category: string
    subcategory: string
    account_code: string
  }) => {
    await createSubcategory.mutateAsync({
      statement_type: draft.statement_type,
      main_category: draft.main_category,
      subcategory_name: draft.subcategory,
      account_code: draft.account_code,
    })
  }

  const requestDelete = (c: PlaidConnection) =>
    setConfirmDelete(c)

  const [confirmDelete, setConfirmDelete] = useState<PlaidConnection | null>(null)

  const doDelete = async (c: PlaidConnection) => {
    try {
      await deleteConn.mutateAsync(c.id)
      toast({ title: 'Connection deleted.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
    }
  }

  const openSchedule = (c: PlaidConnection) => {
    setScheduleFor(c)
    if (c.sync_interval_minutes && c.next_sync_at) {
      const m = c.sync_interval_minutes
      if (m % 1440 === 0) {
        setSchedValue(String(m / 1440))
        setSchedUnit('days')
      } else if (m % 60 === 0) {
        setSchedValue(String(m / 60))
        setSchedUnit('hours')
      } else {
        setSchedValue(String(m))
        setSchedUnit('minutes')
      }
      const t = new Date(c.next_sync_at)
      let h = t.getHours()
      const am = h >= 12 ? 'PM' : 'AM'
      h = h % 12 || 12
      setSchedHour(String(h))
      setSchedMinute(String(t.getMinutes()))
      setSchedAm(am)
    } else {
      setSchedValue('1')
      setSchedUnit('days')
      setSchedHour('8')
      setSchedMinute('0')
      setSchedAm('AM')
    }
  }

  const submitSchedule = async () => {
    if (!scheduleFor) return
    let hour = parseInt(schedHour, 10)
    const minute = parseInt(schedMinute, 10)
    if (schedAm === 'PM' && hour < 12) hour += 12
    if (schedAm === 'AM' && hour === 12) hour = 0
    const now = new Date()
    const next = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute)
    if (next <= now) next.setDate(next.getDate() + 1)
    try {
      await scheduleConn.mutateAsync({
        id: scheduleFor.id,
        body: { value: parseInt(schedValue, 10), unit: schedUnit, nextSyncISO: next.toISOString() },
      })
      toast({ title: 'Schedule saved.' })
      setScheduleFor(null)
    } catch (err) {
      toast({ variant: 'destructive', title: 'Save failed', description: errMsg(err) })
    }
  }

  const isBusy = step === 'loading' || step === 'saving'

  return (
    <Page
      title="Bank Feeds (Plaid)"
      description="Connect your accounts via Plaid for automatic transaction imports."
      width="default"
    >
      {/* Existing connections */}
      {connectionsQ.isLoading ? (
        <Card>
          <CardContent className="py-10">
            <LoadingSpinner />
          </CardContent>
        </Card>
      ) : connections.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Connected accounts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {connections.map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border p-3"
              >
                <div className="flex items-center gap-3">
                  {c.institution_logo ? (
                    <img
                      src={`data:image/png;base64,${c.institution_logo}`}
                      alt=""
                      className="size-10 rounded-md border"
                    />
                  ) : (
                    <div className="flex size-10 items-center justify-center rounded-md bg-muted">
                      <Landmark className="size-5 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <div className="font-semibold">{c.institution_name}</div>
                    <div className="text-xs text-muted-foreground">
                      Added {new Date(c.created_at).toLocaleString()}
                    </div>
                    {c.sync_interval_minutes && c.next_sync_at ? (
                      <div className="text-xs text-primary">
                        <Clock className="mr-0.5 inline size-3" />
                        Next sync: {new Date(c.next_sync_at).toLocaleString()}
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground">
                        <Clock className="mr-0.5 inline size-3" />
                        Not scheduled
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openSchedule(c)}>
                    <Calendar className="size-3" />
                    Schedule
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => syncOne(c.id)}
                    disabled={isBusy}
                  >
                    <RefreshCw className="size-3" />
                    Sync
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => requestDelete(c)}>
                    <Trash2 className="size-3" />
                    Delete
                  </Button>
                </div>
              </div>
            ))}
            <Toolbar className="justify-end">
              <Button onClick={syncAll} disabled={isBusy}>
                <RefreshCw className="size-4" />
                Sync all (last 30 days)
              </Button>
            </Toolbar>
          </CardContent>
        </Card>
      ) : null}

      {/* New connection */}
      {step === 'idle' || step === 'loading' ? (
        <Card>
          <CardHeader>
            <CardTitle>Add new connection / import historical data</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <FormField
                label="Statement Type"
                control={
                  <Select
                    value={statementType || undefined}
                    onValueChange={(v) => setStatementType(v as 'BS' | 'CCS')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BS">Bank Statement</SelectItem>
                      <SelectItem value="CCS">Credit Card Statement</SelectItem>
                    </SelectContent>
                  </Select>
                }
              />
              <FormField
                label="From"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.currentTarget.value)}
              />
              <FormField
                label="To"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.currentTarget.value)}
              />
            </div>
            <Button onClick={startConnect} disabled={isBusy}>
              {isBusy ? <Loader2 className="size-4 animate-spin" /> : <Plug className="size-4" />}
              Connect and import
            </Button>
            {step === 'loading' ? (
              <p className="text-sm text-primary">{busyText}</p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* Classify step */}
      {step === 'classify' ? (
        <ClassifyStepper
          groups={groups}
          selections={selections}
          taxonomy={taxonomy}
          onSelect={(key, sel) => setSelections((prev) => ({ ...prev, [key]: sel }))}
          onClear={(key) => setSelections((prev) => ({ ...prev, [key]: null }))}
          onCreateRecord={handleCreateTaxonomy}
          onSave={saveClassifications}
          onCancel={() => {
            setStep('idle')
            setGroups([])
            setSelections({})
            setClassifiedTables([])
          }}
        />
      ) : null}

      {step === 'saving' ? (
        <Card>
          <CardContent className="space-y-3 py-10 text-center">
            <LoadingSpinner />
            <p className="text-sm text-primary">{busyText}</p>
          </CardContent>
        </Card>
      ) : null}

      {/* Terms modal */}
      <Modal
        open={termsOpen}
        onOpenChange={setTermsOpen}
        title="Plaid Integration Consent"
        footer={
          <>
            <Button variant="secondary" onClick={() => setTermsOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                localStorage.setItem(PLAID_TERMS_KEY, 'true')
                setTermsOpen(false)
                void startConnect()
              }}
            >
              I Accept
            </Button>
          </>
        }
      >
        <div className="space-y-2 text-sm">
          <p>By connecting your financial institution via Plaid, you agree:</p>
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            <li>You grant Bokapsys permission to access account details and transaction history through Plaid.</li>
            <li>Information is used solely for accounting automation. We do not sell personal data.</li>
            <li>Plaid handles credentials securely; Bokapsys never stores your bank password.</li>
            <li>You remain responsible for verifying statement accuracy.</li>
            <li>You may revoke access by deleting the connection here.</li>
          </ul>
        </div>
      </Modal>

      <Modal.Alert
        open={limitOpen}
        onOpenChange={setLimitOpen}
        title="Limit reached"
        body="You have reached your monthly transaction limit. Upgrade to continue syncing."
        onClose={() => navigate('/subscriptions')}
        closeLabel="View plans"
      />

      <Modal
        open={!!scheduleFor}
        onOpenChange={(v) => !v && setScheduleFor(null)}
        title={`Set sync schedule${scheduleFor ? `: ${scheduleFor.institution_name}` : ''}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setScheduleFor(null)}>
              Cancel
            </Button>
            <Button onClick={submitSchedule} disabled={scheduleConn.isPending}>
              Save schedule
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <FormField
            label="Sync every"
            control={
              <div className="flex gap-2">
                <Input
                  type="number"
                  min={1}
                  value={schedValue}
                  onChange={(e) => setSchedValue(e.currentTarget.value)}
                  className="w-24"
                />
                <Select value={schedUnit} onValueChange={(v) => setSchedUnit(v as 'minutes' | 'hours' | 'days')}>
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minutes">Minutes</SelectItem>
                    <SelectItem value="hours">Hours</SelectItem>
                    <SelectItem value="days">Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            }
          />
          <FormField
            label="Start time"
            control={
              <div className="flex items-center gap-2">
                <Select value={schedHour} onValueChange={setSchedHour}>
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }).map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>
                        {String(i + 1).padStart(2, '0')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="font-bold">:</span>
                <Select value={schedMinute} onValueChange={setSchedMinute}>
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 60 }).map((_, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {String(i).padStart(2, '0')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={schedAm} onValueChange={(v) => setSchedAm(v as 'AM' | 'PM')}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AM">AM</SelectItem>
                    <SelectItem value="PM">PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            }
          />
        </div>
      </Modal>

      <Modal.Confirm
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        title="Delete connection?"
        body="This will not delete any transactions that have already been imported."
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={async () => {
          const c = confirmDelete
          setConfirmDelete(null)
          if (c) await doDelete(c)
        }}
      />

      <Modal.Alert
        open={redirectModal}
        onOpenChange={setRedirectModal}
        title="Transactions saved!"
        body="Your transactions have been saved with COA mappings. Redirecting to Statements…"
        onClose={() => navigate('/view-statements')}
        closeLabel="Go now"
      />
    </Page>
  )
}

